import _defineProperty from "@babel/runtime/helpers/defineProperty";
import { createComponentManifestResolver } from '@wix/component-protocol/resolver';
import { createEditorPlatformManifestServicesError, EditorPlatformManifestServicesErrorCode } from './errors';
export class ManifestStore {
  constructor(_ManifestUrlResolver) {
    this._ManifestUrlResolver = _ManifestUrlResolver;
    _defineProperty(this, "_store", new Map());
    _defineProperty(this, "_loadManifestsCache", new Map());
    _defineProperty(this, "_resolvedManifests", new Set());
    _defineProperty(this, "initialLoad", null);
    this.getManifest = this.getManifest.bind(this);
    this.setManifest = this.setManifest.bind(this);
  }
  isResolvedManifest(componentType) {
    return this._resolvedManifests.has(componentType);
  }
  async _loadManifestFromCDN(url) {
    try {
      const response = await fetch(url, {
        headers: {
          'x-wix-extended-timeout': '1'
        }
      });
      return await response.json();
    } catch (err) {
      throw createEditorPlatformManifestServicesError(EditorPlatformManifestServicesErrorCode.LoadManifestFromCDNError, url).withParentError(err);
    }
  }
  async _loadManifest(url) {
    const promiseFromCache = this._loadManifestsCache.get(url);
    if (promiseFromCache) {
      return promiseFromCache;
    }
    const loadManifestPromise = this._loadManifestFromCDN(url);
    this._loadManifestsCache.set(url, loadManifestPromise);
    loadManifestPromise.finally(() => {
      this._loadManifestsCache.delete(url);
    });
    return loadManifestPromise;
  }
  isExternalComponent(compType) {
    return compType.startsWith('platform.builder');
  }
  setManifest(key, manifest) {
    this._store.set(key, manifest);
  }
  setInitialLoad(promise) {
    this.initialLoad = promise;
  }
  async loadManifestsByCompTypes(compTypes, hardUpdate) {
    const errors = [];
    const compTypesToLoad = hardUpdate ? compTypes : compTypes.filter(compType => !this._store.has(compType));
    const loadManifest = async compType => {
      const manifestUrl = this._ManifestUrlResolver.getManifestUrlByCompType(compType);
      if (!manifestUrl) {
        errors.push(createEditorPlatformManifestServicesError(EditorPlatformManifestServicesErrorCode.ManifestUrlNotProvided, compType));
        return null;
      }
      try {
        const manifest = await this._loadManifest(manifestUrl);
        this.setManifest(compType, manifest);
        return compType;
      } catch (err) {
        errors.push(err);
        return null;
      }
    };
    const compTypesToReturn = await Promise.all(compTypesToLoad.map(loadManifest));
    if (errors.length > 0) {
      throw createEditorPlatformManifestServicesError(EditorPlatformManifestServicesErrorCode.MultipleManifestsLoadingFailed, 'Loading manifests by comp types failed').withMultipleErrors(errors);
    }
    return compTypesToReturn.filter(Boolean);
  }
  async resolveManifestsByCompTypes(compTypes) {
    const errors = [];
    const resolver = createComponentManifestResolver(this.getManifestAsync.bind(this));
    const resolveManifest = async compType => {
      try {
        const manifest = this.getManifest(compType);
        if (this.isResolvedManifest(compType)) {
          return null;
        }
        const resolvedManifest = await resolver(manifest);
        this.setManifest(compType, resolvedManifest);
        this._resolvedManifests.add(compType);
        return resolvedManifest;
      } catch (err) {
        errors.push(createEditorPlatformManifestServicesError(EditorPlatformManifestServicesErrorCode.FailedToResolveManifest).withParentError(err).withComponentType(compType));
        return null;
      }
    };
    await this.awaitForInitialLoad();
    await Promise.all(compTypes.map(resolveManifest));
    if (errors.length > 0) {
      throw createEditorPlatformManifestServicesError(EditorPlatformManifestServicesErrorCode.MultipleManifestsResolvingFailed, errors.map(e => e.message).join(', '));
    }
    return compTypes;
  }
  async awaitForInitialLoad() {
    await this.initialLoad;
  }
  getManifest(key) {
    const manifest = this._store.get(key);
    if (manifest) {
      return manifest;
    }
    return null;
  }
  async getManifestAsync(key) {
    await this.awaitForInitialLoad();
    let manifest = this.getManifest(key);
    if (manifest) {
      return manifest;
    } else if (this.isExternalComponent(key)) {
      const manifestUrl = this._ManifestUrlResolver.getManifestUrlByCompType(key);
      if (!manifestUrl) {
        throw createEditorPlatformManifestServicesError(EditorPlatformManifestServicesErrorCode.ManifestUrlNotProvided, key).withComponentType(key);
      }
      manifest = await this._loadManifest(manifestUrl);
      this.setManifest(key, manifest);
      return manifest;
    } else {
      throw createEditorPlatformManifestServicesError(EditorPlatformManifestServicesErrorCode.ComponentNotExistsInEERegistry, 'externalComponentManifest was not loaded from the EE registry and not found in the store').withComponentType(key);
    }
  }
  getResolvedManifest(key) {
    const manifest = this._store.get(key);
    if (!manifest || !this.isResolvedManifest(key)) {
      return null;
    }
    return manifest;
  }
  getResolvedManifestsByCompTypes(compTypes) {
    return compTypes.reduce((acc, compType) => {
      const manifest = this.getResolvedManifest(compType);
      if (manifest) {
        acc[compType] = manifest;
      }
      return acc;
    }, {});
  }
}
//# sourceMappingURL=manifestStore.js.map